/**
 * App-wide location reporter.
 *
 * Runs from RootNavigator on a heartbeat (EVERY screen, not just Home), so the admin
 * live map + the customer /track page stay current whenever the driver has the app open
 * and location enabled. Posts to /api/drivers/me/location, which updates the driver's
 * lastKnownLat/Lng + lastActiveAt AND drops a per-job GPS breadcrumb for any active
 * delivery they're the effective tracking driver for.
 *
 * Previously the only location loop lived inside HomeScreen, so the moment a driver
 * navigated to any other tab (Jobs, Active job, Profile…) their location stopped
 * updating — which is why the admin showed "no location in days/weeks" even though the
 * driver had the app open with location granted.
 */
import { Platform } from 'react-native';
import * as Location from 'expo-location';
import { api } from './api';

function postLoc(coords: { latitude: number; longitude: number; heading?: number | null; speed?: number | null }): Promise<boolean> {
  // Range-validate before sending so we never poison the map/ETA with 0,0 or garbage.
  const { latitude, longitude } = coords;
  if (typeof latitude !== 'number' || typeof longitude !== 'number' || !isFinite(latitude) || !isFinite(longitude)) return Promise.resolve(false);
  if (latitude === 0 && longitude === 0) return Promise.resolve(false);
  if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) return Promise.resolve(false);
  // Returns true once the fix is committed (which also sets the driver's locationGranted on
  // the backend — what the head-out gate checks). Best-effort: false on any failure.
  return api.post('/drivers/me/location', {
    latitude,
    longitude,
    heading: coords.heading ?? undefined,
    speed: coords.speed ?? undefined,
  }).then(() => true).catch(() => false);
}

/**
 * Best-effort: fetch the current position (WITHOUT prompting) and report it.
 * `hasActive` → high accuracy + tighter cache (smooth en-route breadcrumbs);
 * idle → low power. Returns true if a fix was reported. Never throws.
 */
export async function sendLocationNow(hasActive: boolean): Promise<boolean> {
  try {
    if (Platform.OS === 'web') {
      if (typeof navigator === 'undefined' || !navigator.geolocation) return false;
      return await new Promise<boolean>((resolve) => {
        navigator.geolocation.getCurrentPosition(
          (pos) => { postLoc(pos.coords as any); resolve(true); },
          () => resolve(false),
          { enableHighAccuracy: hasActive, maximumAge: hasActive ? 15000 : 60000, timeout: 12000 },
        );
      });
    }
    // Native: only report if permission is already granted (don't prompt on a heartbeat).
    const { status } = await Location.getForegroundPermissionsAsync();
    if (status !== 'granted') return false;
    const pos = await Location.getCurrentPositionAsync({ accuracy: hasActive ? Location.Accuracy.Balanced : Location.Accuracy.Low });
    postLoc(pos.coords as any);
    return true;
  } catch {
    return false;
  }
}

// ─── Continuous tracking (as-much-as-possible while the app is open) ──────────
// On WEB this is the maximum achievable: browsers stop all JS — including geolocation —
// the moment the tab/app is closed or the phone locks, so there is no true
// "track when closed" on the PWA. watchPosition streams fixes continuously while the
// app is open & foreground (every screen). A timer backstop keeps a STATIONARY driver
// fresh (whose watch may not fire). True background-when-closed requires the native app
// build with "Always" location permission (see backgroundLocation.ts).
let _watchId: number | null = null;
let _backstop: any = null;
let _lastCoords: { latitude: number; longitude: number; heading?: number | null; speed?: number | null } | null = null;
let _lastPostAt = 0;
let _hasActive: () => boolean = () => false;

function maybePost(coords: any, force = false) {
  _lastCoords = coords;
  const now = Date.now();
  const minGap = _hasActive() ? 10000 : 30000; // active delivery → tighter cadence
  if (force || now - _lastPostAt >= minGap) { _lastPostAt = now; postLoc(coords); }
}

/** Start continuous location tracking for as long as the driver is logged in. */
export function startLocationTracking(getHasActive: () => boolean) {
  _hasActive = getHasActive;
  if (Platform.OS === 'web') {
    if (typeof navigator === 'undefined' || !navigator.geolocation) return;
    if (_watchId == null) {
      try {
        _watchId = navigator.geolocation.watchPosition(
          (pos) => maybePost(pos.coords as any),
          () => { /* ignore transient errors; backstop + heartbeat retry */ },
          { enableHighAccuracy: true, maximumAge: 10000, timeout: 25000 },
        ) as any;
      } catch { /* geolocation unavailable */ }
    }
    if (!_backstop) {
      _backstop = setInterval(() => {
        // Re-post the latest fix so a stationary driver stays fresh; if the watch has
        // never produced a fix, take a one-shot reading.
        if (_lastCoords) postLoc(_lastCoords);
        else sendLocationNow(_hasActive());
      }, 30000);
    }
  } else {
    // Native foreground: poll on a timer (background-when-closed is handled by the
    // per-job expo background task in backgroundLocation.ts).
    if (!_backstop) {
      _backstop = setInterval(() => { sendLocationNow(_hasActive()); }, 30000);
    }
    sendLocationNow(_hasActive());
  }
}

/** Stop continuous tracking (call on logout). */
export function stopLocationTracking() {
  try {
    if (Platform.OS === 'web' && _watchId != null && typeof navigator !== 'undefined' && navigator.geolocation) {
      navigator.geolocation.clearWatch(_watchId);
    }
  } catch { /* ignore */ }
  _watchId = null;
  if (_backstop) { clearInterval(_backstop); _backstop = null; }
  _lastCoords = null;
}

/**
 * Ensure location is enabled, PROMPTING if needed. Returns true only when we obtained a
 * fix and reported it (which also marks the driver locationGranted=true on the backend).
 * Used to GATE accepting + starting a job — a driver must have location on to do either.
 */
export async function ensureLocationEnabled(): Promise<boolean> {
  try {
    if (Platform.OS === 'web') {
      if (typeof navigator === 'undefined' || !navigator.geolocation) return false;
      return await new Promise<boolean>((resolve) => {
        navigator.geolocation.getCurrentPosition(
          // Await the fix POST so the backend has the driver's location (sets locationGranted)
          // BEFORE the accept/start call that follows — otherwise the head-out gate could
          // race a fire-and-forget post and false-block. Resolve true regardless of the
          // POST result (permission is granted; a transient network blip shouldn't block).
          (pos) => { postLoc(pos.coords as any).finally(() => resolve(true)); },
          async (err: any) => {
            // GATE ON PERMISSION, NOT on getting a fast fix. Only a TRUE denial (code 1)
            // blocks. A timeout / position-unavailable (code 2/3) on a permitted device —
            // indoors, in a warehouse, cold GPS — must NOT stop a driver who HAS location
            // enabled (that was generating false "enable location" failures). Re-check the
            // permission state and allow unless it's actually denied.
            if (err && err.code === 1) { resolve(false); return; }
            try {
              if ((navigator as any).permissions?.query) {
                const st = await (navigator as any).permissions.query({ name: 'geolocation' });
                resolve(st.state !== 'denied');
                return;
              }
            } catch { /* permissions API unavailable */ }
            resolve(true); // permission not denied, just no quick fix — don't block
          },
          // Accept a recent cached fix so an active driver passes instantly.
          { enableHighAccuracy: true, timeout: 15000, maximumAge: 600000 },
        );
      });
    }
    const { status } = await Location.requestForegroundPermissionsAsync();
    if (status !== 'granted') return false;
    // Permission granted = enabled. AWAIT a fresh fix post when we can get one (so the backend
    // has the driver's location before the accept/start that follows), but DON'T block the
    // action if the GPS itself is slow/unavailable — a permitted driver shouldn't be stuck.
    try {
      const pos = await Location.getCurrentPositionAsync({ accuracy: Location.Accuracy.Balanced });
      await postLoc(pos.coords as any);
    } catch { /* slow/indoor GPS — permission is granted, proceed */ }
    return true;
  } catch {
    return false;
  }
}
